/**
 * Общее для обеих прогулок — маршрута редакции и своего маршрута:
 * расстояния, форматы и пеший путь. Брендбук 2.11 и 6.2
 */
import { fetchFootRoute } from '../services/api';

// Через сколько метров сдвига перестраивать пеший путь до цели
export const USER_PATH_REFETCH_M = 25;
// Дальше этого от линии пути — человек сошёл с неё, путь строится заново
// сразу, не дожидаясь USER_PATH_REFETCH_M
export const OFF_PATH_M = 30;

// Расстояние от точки до отрезка ломаной и длина отрезка, в метрах
export const segmentMeasure = (pt, a, b) => {
  const kx = 111320 * Math.cos((pt[0] * Math.PI) / 180);
  const ky = 110540;
  const ax = (a[1] - pt[1]) * kx;
  const ay = (a[0] - pt[0]) * ky;
  const dx = (b[1] - pt[1]) * kx - ax;
  const dy = (b[0] - pt[0]) * ky - ay;
  const len2 = dx * dx + dy * dy;
  const t = len2 ? Math.max(0, Math.min(1, -(ax * dx + ay * dy) / len2)) : 0;
  return { dist: Math.hypot(ax + t * dx, ay + t * dy), length: Math.sqrt(len2) };
};

/**
 * Ближайший к точке отрезок ломаной: расстояние до него в метрах и его
 * номер. Считается по отрезкам, а не по вершинам: на прямой улице
 * вершины пути бывают в сотнях метров друг от друга
 */
export const nearestOnPath = (pt, path) => {
  let best = { dist: Infinity, seg: 0 };
  for (let i = 0; i < path.length - 1; i++) {
    const { dist } = segmentMeasure(pt, path[i], path[i + 1]);
    if (dist < best.dist) best = { dist, seg: i };
  }
  return best;
};

// Сколько начала пути просматривать, обрезая крюк у старта. 250 м, а не
// 150: у «Сенной площади» сервис ведёт 91 м на юг и столько же обратно —
// обратный проход начинается дальше 150 м и в просмотр не попадал
export const TRIM_LOOKAHEAD_M = 250;
// Насколько дальше самого близкого прохода может быть обратный проход
// крюка, чтобы считаться «тем же местом»
export const TRIM_TOLERANCE_M = 15;

/**
 * Путь до цели, начинающийся от точки старта — человека или станции
 * метро: пройденное начало и крюк у старта отрезаются.
 *
 * Берётся не ближайший отрезок, а самый дальний по ходу из тех, что
 * почти так же близко. Сервис маршрутов «примагничивает» старт к дорожке
 * рядом, и если на карте OSM она не соединена с улицами в нужную сторону,
 * путь начинается крюком туда и обратно мимо старта. С ближайшим отрезком
 * крюк торчал хвостом. Смотрим только начало пути: дальше маршрут может
 * снова пройти рядом, и срезать его целиком нельзя. Старт далеко
 * от пути — null
 */
export const trimPathToUser = (pt, path) => {
  const near = [];
  let best = Infinity;
  let walked = 0;
  for (let i = 0; i < path.length - 1 && walked <= TRIM_LOOKAHEAD_M; i++) {
    const { dist, length } = segmentMeasure(pt, path[i], path[i + 1]);
    near.push([i, dist]);
    best = Math.min(best, dist);
    walked += length;
  }
  if (best > OFF_PATH_M) return null;
  let seg = 0;
  near.forEach(([i, dist]) => { if (dist <= best + TRIM_TOLERANCE_M) seg = i; });
  return [pt, ...path.slice(seg + 1)];
};

// ~1350 шагов на 1 км (средний шаг 74 см)
export const calcSteps = (km) => km > 0 ? Math.round(km * 1350) : null;

/**
 * Время в пути словами. Раньше было «21:53» — на прогулке это читалось
 * как время суток, а не «21 минута в пути». Секунды на ходу не нужны
 */
export const formatElapsed = (ms) => {
  const min = Math.floor(ms / 60000);
  if (min < 1) return '<1 мин';
  if (min < 60) return `${min} мин`;
  const h = Math.floor(min / 60);
  const rest = min % 60;
  return rest ? `${h} ч ${rest} мин` : `${h} ч`;
};

export const calcDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371000;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2
    + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

// Прикидка по прямой — со знаком «~» и десятичной запятой (брендбук 6.2)
export const formatDist = (m) => {
  if (m < 1000) return `~${Math.round(m / 50) * 50 || 50} м`;
  if (m < 100000) return `~${(m / 1000).toFixed(1).replace('.', ',')} км`;
  return `~${Math.round(m / 1000)} км`;
};

// Дальше этого пешком не ходят — время идти не показываем
export const WALKABLE_MAX_M = 10000;

// Пешеход идёт ~4.5 км/ч. Свыше часа минуты не нужны
export const formatWalkTime = (m) => {
  const min = Math.round(m / 75);
  if (min < 1) return null;
  if (min < 60) return `${min} мин пешком`;
  const h = Math.floor(min / 60);
  const rest = min % 60;
  return rest ? `${h} ч ${rest} мин пешком` : `${h} ч пешком`;
};

/**
 * Пеший маршрут — через наш бэкенд.
 *
 * Раньше запрос уходил прямо на routing.openstreetmap.de, и позиция
 * пользователя подставлялась в путь URL: точка «где человек стоит»
 * оседала в чужих access-логах вместе с его IP, а по череде запросов
 * складывался трек. Теперь во внешнюю сеть ходит сервер.
 */
export const fetchOsrmRoute = async (points) => {
  if (points.length < 2) return null;
  return fetchFootRoute(points);
};

